import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type ButtonHTMLAttributes, type ReactElement, type ReactNode, useId } from 'react';

/** Visual drag affordance for bottom sheets (C8). */
export function DragHandle({
  className,
  disabled = false,
  label = 'جابه‌جایی',
  onPointerDown,
  ...rest
}: {
  readonly disabled?: boolean;
  readonly label?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      aria-label={label}
      className={['saay-drag-handle', className].filter(Boolean).join(' ')}
      disabled={disabled}
      onPointerDown={onPointerDown}
      type="button"
    />
  );
}

export interface AlertDialogProps {
  readonly cancelLabel?: string;
  readonly children?: ReactNode;
  readonly confirmLabel: string;
  readonly description?: ReactNode;
  readonly destructive?: boolean;
  /** The confirm button does not close the dialog; the caller closes it after the work succeeded. */
  readonly keepOpenOnConfirm?: boolean;
  readonly onConfirm: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly title: ReactNode;
}

/** Centered alert dialog (C5): no dismiss control, Esc cancels, safe action focused first. */
export function AlertDialog({
  cancelLabel = 'بازگشت',
  children,
  confirmLabel,
  description,
  destructive = false,
  keepOpenOnConfirm = false,
  onConfirm,
  onOpenChange,
  open,
  title,
}: AlertDialogProps) {
  const confirmButton = (
    <button
      className="saay-button saay-state"
      data-appearance="filled"
      data-destructive={destructive || undefined}
      onClick={onConfirm}
      type="button"
    >
      <span className="saay-button__label">{confirmLabel}</span>
    </button>
  );
  return (
    <DialogPrimitive.Root onOpenChange={onOpenChange} open={open}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="saay-alert-dialog__overlay" />
        <DialogPrimitive.Content
          // The same Radix dialog build as `Dialog`, so stacked layers share one focus scope stack
          // (the separate alert-dialog package pulled in a second copy that fought for focus).
          className="saay-alert-dialog__content"
          dir="rtl"
          onInteractOutside={(event) => event.preventDefault()}
          role="alertdialog"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const cancel = document.querySelector<HTMLButtonElement>('[data-saay-alert-cancel]');
            cancel?.focus();
          }}
        >
          <DialogPrimitive.Title className="saay-alert-dialog__title">
            {title}
          </DialogPrimitive.Title>
          {description === undefined ? null : (
            <DialogPrimitive.Description className="saay-alert-dialog__description">
              {description}
            </DialogPrimitive.Description>
          )}
          {children}
          <div className="saay-alert-dialog__actions">
            <DialogPrimitive.Close asChild>
              <button
                className="saay-button saay-state"
                data-appearance="text"
                data-saay-alert-cancel
                type="button"
              >
                <span className="saay-button__label">{cancelLabel}</span>
              </button>
            </DialogPrimitive.Close>
            {keepOpenOnConfirm ? (
              confirmButton
            ) : (
              <DialogPrimitive.Close asChild>{confirmButton}</DialogPrimitive.Close>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Discard-changes guard (X4) built on the alert dialog. */
export function DiscardChangesDialog({
  onConfirmDiscard,
  onOpenChange,
  open,
}: {
  readonly onConfirmDiscard: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  return (
    <AlertDialog
      confirmLabel="خروج بدون ذخیره"
      description="تغییراتی که ذخیره نشده‌اند از بین می‌روند."
      destructive
      onConfirm={onConfirmDiscard}
      onOpenChange={onOpenChange}
      open={open}
      title="تغییرات ذخیره نشود؟"
    />
  );
}

export function Popover({
  children,
  content,
  onOpenChange,
  open,
  trigger,
}: {
  readonly children?: ReactNode;
  readonly content: ReactNode;
  readonly onOpenChange?: (open: boolean) => void;
  readonly open?: boolean;
  readonly trigger: ReactElement;
}) {
  return (
    <PopoverPrimitive.Root
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
      {...(open === undefined ? {} : { open })}
    >
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className="saay-popover__content" dir="rtl" sideOffset={6}>
          {content}
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function TooltipProvider({
  children,
  delayDuration = 400,
}: {
  readonly children: ReactNode;
  readonly delayDuration?: number;
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>{children}</TooltipPrimitive.Provider>
  );
}

export function Tooltip({
  children,
  content,
}: {
  readonly children: ReactElement;
  readonly content: ReactNode;
}) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="saay-tooltip__content" dir="rtl" sideOffset={6}>
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export function RichTooltip({
  children,
  description,
  title,
}: {
  readonly children: ReactElement;
  readonly description?: ReactNode;
  readonly title: ReactNode;
}) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className="saay-tooltip__content"
          data-rich="true"
          dir="rtl"
          sideOffset={6}
        >
          <p className="saay-tooltip__title">{title}</p>
          {description === undefined ? null : (
            <p className="saay-tooltip__description">{description}</p>
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export function DropdownMenu({
  align = 'end',
  children,
  label,
  onOpenChange,
  open,
  trigger,
}: {
  readonly align?: 'end' | 'start';
  readonly children: ReactNode;
  readonly label: string;
  readonly onOpenChange?: (open: boolean) => void;
  readonly open?: boolean;
  readonly trigger: ReactElement;
}) {
  const menuId = useId();
  return (
    <DropdownMenuPrimitive.Root
      modal={false}
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
      {...(open === undefined ? {} : { open })}
    >
      <DropdownMenuPrimitive.Trigger
        aria-label={label}
        asChild
        className="saay-dropdown-menu__trigger"
      >
        {trigger}
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align === 'start' ? 'start' : 'end'}
          aria-label={label}
          className="saay-dropdown-menu__content"
          id={menuId}
          sideOffset={6}
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

export function DropdownMenuItem({
  children,
  destructive = false,
  disabled = false,
  onSelect,
}: {
  readonly children: ReactNode;
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly onSelect?: () => void;
}) {
  return (
    <DropdownMenuPrimitive.Item
      className="saay-dropdown-menu__item"
      data-destructive={destructive || undefined}
      disabled={disabled}
      onSelect={(event) => {
        event.preventDefault();
        onSelect?.();
      }}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

/** Pinned bottom actions for long forms on compact layouts. */
export function StickyBottomActionBar({
  children,
  fixed = false,
}: {
  readonly children: ReactNode;
  readonly fixed?: boolean;
}) {
  return (
    <div className="saay-sticky-action-bar" data-fixed={fixed || undefined} role="toolbar">
      {children}
    </div>
  );
}
